import { useState } from 'react';
import { Stack } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import StyledButton from '../Button';
import { PRIVACY_POLICY_URL, TERMS_AND_CONDITIONS_URL } from '../../constants';
import feedbackApi from '../../api/feedbackApi';
import NameField from './components/NameField';
import EmailField from './components/EmailField';
import TrackGenerationsField from './components/TrackGenerationsField';
import AdditionalInfoField from './components/AdditionalInfoField';
import {
  StyledButtonContainer,
  StyledCloseButton,
  StyledDescription,
  StyledDialog,
  StyledDialogContent,
  StyledDisclaimer,
  StyledLink,
  StyledTitle,
} from './styled';

type PropsType = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  onError?: () => void;
};

const FeedbackModal = ({ isOpen, onClose, onSuccess, onError }: PropsType) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [desiredMonthlyTrackGenerations, setSetSesiredMonthlyTrackGenerations] =
    useState('');
  const [additionalInformation, setAdditionalInformation] = useState('');

  const [isNameValid, setIsNameValid] = useState(false);
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [isTrackGenerationsValid, setIsTrackGenerationsValid] = useState(false);
  const [isAdditionalInfoValid, setIsAdditionalInfoValid] = useState(false);

  const [sendFeedback, { isLoading }] = feedbackApi.useSendFeedbackMutation();

  const isFormValid =
    isNameValid &&
    isEmailValid &&
    isTrackGenerationsValid &&
    isAdditionalInfoValid;

  const clearFormData = () => {
    setName('');
    setEmail('');
    setSetSesiredMonthlyTrackGenerations('');
    setAdditionalInformation('');
  };

  const handleSubmit = async () => {
    if (!isFormValid) {
      return;
    }

    try {
      await sendFeedback({
        name,
        email,
        desiredMonthlyTrackGenerations,
        additionalInformation,
      }).unwrap();

      clearFormData();
      onSuccess?.();
    } catch {
      onError?.();
    }
  };

  return (
    <StyledDialog open={isOpen} onClose={onClose}>
      <StyledCloseButton onClick={onClose} aria-label="close">
        <CloseIcon />
      </StyledCloseButton>
      <StyledDialogContent>
        <Stack spacing={1}>
          <StyledTitle>Contact Sales</StyledTitle>
          <StyledDescription>
            Fill out the form below and our team will get back to you regarding
            your subscription needs as soon as possible.
          </StyledDescription>
        </Stack>

        <NameField
          disabled={isLoading}
          value={name}
          onChange={setName}
          onValidationChange={setIsNameValid}
        />

        <EmailField
          disabled={isLoading}
          value={email}
          onChange={setEmail}
          onValidationChange={setIsEmailValid}
        />

        <TrackGenerationsField
          disabled={isLoading}
          value={desiredMonthlyTrackGenerations}
          onChange={setSetSesiredMonthlyTrackGenerations}
          onValidationChange={setIsTrackGenerationsValid}
        />

        <AdditionalInfoField
          disabled={isLoading}
          value={additionalInformation}
          onChange={setAdditionalInformation}
          onValidationChange={setIsAdditionalInfoValid}
        />

        <StyledDisclaimer>
          By submitting this form, you agree to our{' '}
          <StyledLink href={PRIVACY_POLICY_URL} target="_blank">
            Privacy Policy
          </StyledLink>{' '}
          and{' '}
          <StyledLink href={TERMS_AND_CONDITIONS_URL} target="_blank">
            Terms & Conditions
          </StyledLink>
          . Your details will not be shared with third parties.
        </StyledDisclaimer>

        <StyledButtonContainer>
          <StyledButton
            loading={isLoading}
            onClick={handleSubmit}
            disabled={!isFormValid || isLoading}
          >
            Send
          </StyledButton>
        </StyledButtonContainer>
      </StyledDialogContent>
    </StyledDialog>
  );
};

export default FeedbackModal;
